import { useEffect, useState } from "react";
import { MapPin, Smartphone } from "lucide-react";
import { History, Loader2, ArrowRight, Download, FileText } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

function InstallAttachments({ materialId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/installations/${materialId}/attachments`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [materialId]);
  const download = async (a) => {
    try {
      const r = await api.get(`/attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.original_filename || "attachment"; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Download failed"); }
  };
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="timeline-attachments">
      {items.map((a) => (
        <Button key={a.id} data-testid={`att-download-${a.id}`} variant="outline" size="sm" onClick={() => download(a)}>
          <FileText className="mr-1 h-3.5 w-3.5" /> {a.original_filename?.slice(0, 18) || "file"} <Download className="ml-1 h-3.5 w-3.5" />
        </Button>
      ))}
    </div>
  );
}

const SOURCE_FIELDS = [
  ["date_received", "Date of Received"],
  ["document_no", "Document No."],
  ["siv_no", "SIV No."],
  ["reservation_no", "Reservation No."],
  ["issued_by", "Issued By"],
  ["circle", "Circle"],
  ["division", "Division"],
  ["subdivision", "Subdivision"],
];

function UploadedDetails({ source }) {
  const rows = SOURCE_FIELDS.filter(([k]) => source[k] != null && String(source[k]).trim() !== "");
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="timeline-uploaded-details">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <FileText className="h-3.5 w-3.5" /> Uploaded Details
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400">No upload details captured for this material.</div>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
          {rows.map(([k, label]) => (
            <div key={k} className="text-sm">
              <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
              <div className="font-medium text-slate-800">{String(source[k])}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TimelineButton({ materialId, serial }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = async () => {
    setOpen(true); setData(null);
    try { const r = await api.get(`/materials/${materialId}/timeline`); setData(r.data); } catch { setData({ events: [] }); }
  };
  return (
    <>
      <Button data-testid={`timeline-btn-${materialId}`} variant="ghost" size="sm" onClick={load}><History className="h-4 w-4" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Material Timeline · {serial}</DialogTitle></DialogHeader>
          {!data ? <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div> : (
            <>
            {data.material?.source && <UploadedDetails source={data.material.source} />}
            <ol className="relative border-l border-slate-200 pl-6" data-testid="timeline-events">
              {(data.events || []).map((e, i) => (
                <li key={i} className="mb-5">
                  <span className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full bg-blue-600" />
                  <div className="flex items-center gap-2">
                    <span className="font-display text-sm font-semibold text-slate-900">{e.action}</span>
                    <div className="flex items-center gap-1 font-mono text-[10px] text-slate-500">
                      <span>{e.previous_status || "—"}</span><ArrowRight className="h-3 w-3" /><span className="text-blue-700">{e.new_status}</span>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{(e.server_ts || "").slice(0, 19).replace("T", " ")} · {e.actor_name || e.actor_email || "system"}</div>
                  {e.remarks && <div className="mt-1 text-sm text-slate-600">{e.remarks}</div>}
                  {(e.geo || e.metadata?.geo) && (() => { const g = e.geo || e.metadata?.geo; return (
                    <a data-testid="timeline-geo" href={`https://maps.google.com/?q=${g.lat},${g.lng}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline">
                      <MapPin className="h-3 w-3" /> {Number(g.lat).toFixed(5)}, {Number(g.lng).toFixed(5)}{g.accuracy ? ` (±${Math.round(g.accuracy)}m)` : ""}
                    </a>
                  ); })()}
                  {e.device && <div data-testid="timeline-device" className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400"><Smartphone className="h-3 w-3" /> {e.device}</div>}
                  {e.metadata?.changed_serial_from && <div className="mt-1"><Badge className="bg-amber-100 text-amber-700">Serial {e.metadata.changed_serial_from} → {e.metadata.changed_serial_to}</Badge></div>}
                  {e.metadata?.actual_received_date && <div className="mt-1 text-xs text-slate-500">Actual received: {e.metadata.actual_received_date}</div>}
                  {e.action === "MATERIAL_INSTALLED_BY_FIELD_USER" && (
                    <div className="mt-1 rounded-lg bg-indigo-50 p-2 text-xs text-indigo-900" data-testid="timeline-install-meta">
                      <div>Consumer: <span className="font-semibold">{e.metadata?.consumer_number || "—"}</span></div>
                      <div>Actual install date: {e.metadata?.actual_installation_date || "—"} · Location: {e.metadata?.location_status || "—"}</div>
                      <div>Attachments: {e.metadata?.attachment_count ?? 0}</div>
                      <InstallAttachments materialId={materialId} />
                    </div>
                  )}
                </li>
              ))}
            </ol>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
